import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';

const AnimatedShaderBackground = () => {
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const scene = new THREE.Scene();
    const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    
    const updateSize = () => {
      const width = container.clientWidth || window.innerWidth;
      const height = container.clientHeight || window.innerHeight;
      renderer.setSize(width, height);
      material.uniforms.iResolution.value.set(width, height);
    };

    const material = new THREE.ShaderMaterial({
      uniforms: {
        iTime: { value: 0 },
        iResolution: { value: new THREE.Vector2(window.innerWidth, window.innerHeight) }
      },
      vertexShader: `
        void main() {
          gl_Position = vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform float iTime;
        uniform vec2 iResolution;

        // Optimized random function
        float rand(vec2 n) {
          return fract(sin(dot(n, vec2(12.9898, 4.1414))) * 43758.5453);
        }

        // Single octave noise for speed
        float noise(vec2 p) {
          vec2 ip = floor(p);
          vec2 u = fract(p);
          u = u*u*(3.0-2.0*u);
          return mix(
            mix(rand(ip), rand(ip + vec2(1.0, 0.0)), u.x),
            mix(rand(ip + vec2(0.0, 1.0)), rand(ip + vec2(1.0, 1.0)), u.x), u.y);
        }

        void main() {
          vec2 uv = gl_FragCoord.xy / iResolution.y;
          vec2 p = (gl_FragCoord.xy - iResolution.xy * 0.5) / iResolution.y * mat2(7.0, -3.0, 3.0, 5.0);
          
          vec4 o = vec4(0.0);
          // Pre-calculate base movement
          float timeShift = iTime * 0.05;

          // Reduced to 85 iterations with optimized math for better performance
          for (float i = 0.0; i < 85.0; i++) {
            // Wider horizontal offset spread
            float horizontalOffset = (i / 85.0 - 0.45) * 45.0;
            
            // Simplified movement math
            float ang = i * i + (iTime + p.x * 0.1) * 0.03 + i;
            vec2 v = p + vec2(horizontalOffset, 0.0) + cos(ang + vec2(13.0, 11.0)) * 4.0;
            
            // Replaced expensive FBM with single noise call for 'tail'
            float tailNoise = noise(v * 0.8 + vec2(iTime * 0.1, i)) * 0.2;
            
            vec3 blue = vec3(0.38, 0.4, 0.94);
            vec3 pink = vec3(0.92, 0.28, 0.6);
            vec3 purple = vec3(0.66, 0.33, 0.94);
            
            float colorShift = sin(i * 0.5 + iTime * 0.8) * 0.5 + 0.5;
            vec3 auroraColors = mix(blue, mix(pink, purple, colorShift), cos(i * 0.2 + iTime * 0.5) * 0.5 + 0.5);
            
            // Optimized thickness and glow
            float d = length(max(abs(v), vec2(abs(v.x) * 0.012, abs(v.y) * 1.3)));
            float intensity = exp(sin(i * 1.5 + iTime * 0.8)) / d;
            
            float fade = smoothstep(0.0, 1.0, i / 85.0) * 0.8;
            o += vec4(auroraColors, 1.0) * intensity * (1.0 + tailNoise) * fade;
          }

          // Balanced tone mapping and final output
          o = tanh(pow(o / 140.0, vec4(1.5)));
          gl_FragColor = o * 2.8; 
        }
      `
    });

    const geometry = new THREE.PlaneGeometry(2, 2);
    const mesh = new THREE.Mesh(geometry, material);
    scene.add(mesh);

    container.appendChild(renderer.domElement);
    updateSize();

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let frameId;
    const animate = () => {
      material.uniforms.iTime.value += 0.01;
      renderer.render(scene, camera);
      // Respeta la preferencia de reducir movimiento: pinta un único fotograma
      // estático en vez de animar en bucle.
      if (!prefersReducedMotion) {
        frameId = requestAnimationFrame(animate);
      }
    };
    animate();

    const handleResize = () => {
      updateSize();
    };
    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener('resize', handleResize);
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
      geometry.dispose();
      material.dispose();
      renderer.dispose();
    };
  }, []);

  return (
    <div 
      ref={containerRef} 
      className="absolute inset-0 pointer-events-none"
      style={{ zIndex: 0, opacity: 0.8 }}
    />
  );
};

export default AnimatedShaderBackground;
